import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { useCallback, useEffect } from "react";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";

import DiscussionEditor from "components/DiscussionEditor";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import TopNavBarMobile from "components/TopNavBar/TopNavBarMobile";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";
import { USE_EMBER_IFRAME } from "consts";

import styles from "./style.module.scss";

const Modal = dynamic(() => import("react-modal"));

type DiscussionComposeModalProps = {
  entity?: any;
  profile?: any;
  collection?: any;
  onClose?: () => void;
  onDiscussionPost?: (_newDiscussion?: any) => void;
  showStartNewDicussion?: boolean;
  style?: "default" | "v2";
};

const DiscussionComposeModal = ({
  entity,
  profile,
  collection,
  onClose,
  onDiscussionPost,
  showStartNewDicussion = false,
  style = "default",
}: DiscussionComposeModalProps) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { account: loggedInAccount } = useAuthentication();

  const toggleModal = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    router.back();
  }, [onClose, router]);

  const onMessage = useCallback(
    (event) => {
      const messageType = event?.data[0];
      switch (messageType) {
        case "discussion-compose-success-post": {
          onDiscussionPost && onDiscussionPost();
          return;
        }
        case "discussion-compose-on-post": {
          return;
        }
      }
    },
    [onDiscussionPost]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [onMessage]);

  const params = [];

  if (entity) {
    params.push(`entity-id=${entity.id}`);
  }

  if (collection) {
    params.push(`collection-id=${collection.id}`);
  }

  const iframeSrc = `${emberAppBaseUrl}/embed/discussion-compose?${params.join(
    "&"
  )}`;

  return (
    <Modal
      portalClassName={styles.modal}
      overlayClassName={styles.overlay}
      className={classNames(
        styles.content,
        mobile ? styles.mobileLayout : styles.desktopLayout
      )}
      isOpen
      onRequestClose={onClose}
      onAfterOpen={() => {
        document.body.style.overflowY = "hidden";
      }}
      onAfterClose={() => {
        document.body.style.overflowY = "";
      }}
    >
      {mobile ? (
        <TopNavBarMobile
          onGoBack={toggleModal}
          customBackButtonLabel="Cancel"
        />
      ) : (
        <div className={styles.actionsBar}>
          <FontAwesomeIcon
            icon={faTimes}
            className={styles.closeButton}
            onClick={onClose}
          />
        </div>
      )}
      <h4
        className={twMerge(
          "text-[18px] font-bold",
          "pb-[2px] mx-[15px] md:pt-[30px] md:mx-[30px] mb-[10px]",
          "border-0 border-b border-solid border-black/20"
        )}
      >
        {showStartNewDicussion && <>Start New Discussion</>}
        {entity && (
          <>
            New Discussion on <b>{entity.prettyName}</b>
          </>
        )}
        {profile && (
          <>
            New Discussion on <b>{profile.name}</b>
          </>
        )}
        {collection && (
          <>
            New Discussion on <b>{collection.name}</b>
          </>
        )}
      </h4>
      <div className="px-[15px] md:px-[30px] pb-[50px]">
        {loggedInAccount?.isInternalUser || !USE_EMBER_IFRAME ? (
          <DiscussionEditor
            commentable={entity || profile || collection}
            afterSubmit={(newDiscussion) =>
              onDiscussionPost && onDiscussionPost(newDiscussion)
            }
            style={style}
          />
        ) : (
          <iframe
            className={classNames("w-full h-[400px] px-[15px] overflow-hidden")}
            src={iframeSrc}
          />
        )}
      </div>
    </Modal>
  );
};

export default DiscussionComposeModal;
